import React, { useState } from "react";
import { CheckCircle2, Send, MessageCircle } from "lucide-react";
import { LinkShell, waLink, fadeUp } from "./LinkShell";

const BUDGETS = ["Kurang dari 10 juta", "10-25 juta", "25-50 juta", "Lebih dari 50 juta"];
const TIMELINES = ["Kurang dari 1 bulan", "1-3 bulan", "3-6 bulan"];
const EMPTY = { nama: "", wa: "", bisnis: "", kebutuhan: "", fitur: "", budget: "", timeline: "" };

const inputCls = "mt-1.5 w-full rounded-xl border border-zinc-200 bg-white px-4 py-3 text-base outline-none transition-[border-color,box-shadow] focus:border-lime-500 focus:ring-4 focus:ring-lime-200 sm:text-sm";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-sm font-semibold text-zinc-800">{label} <span className="text-lime-600">*</span></span>
    {children}
  </label>
);

const buildMessage = (f) =>
  [
    "Halo, saya ingin minta estimasi project aplikasi.", "",
    `*Nama:* ${f.nama}`, `*No. WhatsApp:* ${f.wa}`, `*Jenis Bisnis:* ${f.bisnis}`, "",
    `*Deskripsi Kebutuhan:*`, f.kebutuhan, "",
    `*Fitur Utama:*`, f.fitur, "",
    `*Estimasi Budget:* ${f.budget}`, `*Timeline:* ${f.timeline}`,
  ].join("\n");

function SuccessBox({ url, onReset }) {
  return (
    <div data-testid="estimasi-success" className="rounded-3xl border border-lime-300 bg-lime-50 p-6 text-center">
      <CheckCircle2 className="mx-auto h-12 w-12 text-lime-600" />
      <h2 className="mt-3 text-xl font-extrabold">Terima kasih! Permintaan estimasi terkirim.</h2>
      <p className="mt-2 text-sm text-zinc-600">WhatsApp sudah dibuka dengan ringkasan kebutuhanmu. Tinggal tekan kirim, kami akan segera merespons.</p>
      <a href={url} target="_blank" rel="noopener noreferrer" data-testid="estimasi-open-wa" className="mt-5 inline-flex items-center gap-2 rounded-full bg-lime-400 px-5 py-3 text-sm font-bold text-zinc-950 hover:bg-lime-300">
        <MessageCircle className="h-4 w-4" /> Buka WhatsApp Lagi
      </a>
      <button type="button" onClick={onReset} data-testid="estimasi-reset" className="mt-3 block w-full text-sm font-medium text-zinc-500 underline-offset-4 hover:underline">Isi form baru</button>
    </div>
  );
}

export default function LinkEstimasi() {
  const [f, setF] = useState(EMPTY);
  const [error, setError] = useState("");
  const [sentUrl, setSentUrl] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = (e) => {
    e.preventDefault();
    if (Object.values(f).some((v) => !String(v).trim())) { setError("Mohon lengkapi semua field."); return; }
    if (String(f.wa).replace(/\D/g, "").length < 9) { setError("Nomor WhatsApp tidak valid."); return; }
    setError("");
    const url = waLink(buildMessage(f));
    setSentUrl(url);
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <LinkShell back title="Minta Estimasi — AI Business Builder" testId="link-estimasi-page">
      <div {...fadeUp(0)}>
        <p className="text-xs font-bold uppercase tracking-wider text-lime-700">Estimasi Project</p>
        <h1 className="mt-1 text-3xl font-extrabold tracking-tight">Minta Estimasi Project</h1>
        <p className="mt-2 text-sm text-zinc-500">Ceritakan kebutuhanmu, kami bantu hitung scope, fitur, dan biaya.</p>
      </div>
      <div {...fadeUp(1, "mt-6")}>
        {sentUrl ? (
          <SuccessBox url={sentUrl} onReset={() => { setF(EMPTY); setSentUrl(""); }} />
        ) : (
          <form onSubmit={submit} data-testid="estimasi-form" className="space-y-4 rounded-3xl border border-zinc-200 bg-white p-5 sm:p-6">
            <Field label="Nama"><input data-testid="estimasi-nama" className={inputCls} value={f.nama} onChange={set("nama")} placeholder="Nama lengkap" /></Field>
            <Field label="Nomor WhatsApp"><input data-testid="estimasi-wa" inputMode="tel" className={inputCls} value={f.wa} onChange={set("wa")} placeholder="08xxxxxxxxxx" /></Field>
            <Field label="Jenis Bisnis"><input data-testid="estimasi-bisnis" className={inputCls} value={f.bisnis} onChange={set("bisnis")} placeholder="cth. Klinik kecantikan, F&B, Retail" /></Field>
            <Field label="Deskripsi Kebutuhan Aplikasi"><textarea data-testid="estimasi-kebutuhan" rows={4} className={inputCls} value={f.kebutuhan} onChange={set("kebutuhan")} placeholder="Masalah apa yang ingin diselesaikan?" /></Field>
            <Field label="Fitur Utama yang Diinginkan"><textarea data-testid="estimasi-fitur" rows={3} className={inputCls} value={f.fitur} onChange={set("fitur")} placeholder="cth. Booking online, laporan penjualan, notifikasi WA" /></Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Estimasi Budget">
                <select data-testid="estimasi-budget" className={inputCls} value={f.budget} onChange={set("budget")}>
                  <option value="">Pilih budget</option>
                  {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </Field>
              <Field label="Timeline">
                <select data-testid="estimasi-timeline" className={inputCls} value={f.timeline} onChange={set("timeline")}>
                  <option value="">Pilih timeline</option>
                  {TIMELINES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </Field>
            </div>
            {error && <p data-testid="estimasi-error" className="rounded-xl bg-rose-50 px-4 py-2.5 text-sm font-medium text-rose-700">{error}</p>}
            <button type="submit" data-testid="estimasi-submit" className="flex w-full items-center justify-center gap-2 rounded-full bg-lime-400 px-5 py-4 font-bold text-zinc-950 shadow-[0_10px_30px_-10px_rgba(132,204,22,0.7)] transition-[transform,background-color] duration-200 hover:-translate-y-0.5 hover:bg-lime-300">
              <Send className="h-4 w-4" /> Kirim Permintaan Estimasi
            </button>
          </form>
        )}
      </div>
    </LinkShell>
  );
}
